import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useI18n } from '@/contexts/I18nContext'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher'
import { useState } from 'react'
import {
  LayoutGrid,
  Package,
  Truck,
  Boxes,
  ClipboardList,
  ShoppingCart,
  Target,
  Users,
  Settings,
  ChefHat,
  LogOut,
  Cpu,
  Brain,
  Sparkles,
  Menu,
  X,
} from 'lucide-react'

export const AdminLayout = () => {
  const { logout, user } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const menuItems = [
    { path: '/admin/catalog', label: t('nav.catalog'), icon: LayoutGrid },
    { path: '/admin/digestor', label: t('nav.digestor'), icon: Brain },
    { path: '/admin/inventory', label: t('nav.inventory'), icon: Boxes },
    { path: '/admin/suppliers', label: t('nav.suppliers'), icon: Truck },
    { path: '/admin/products', label: t('nav.products'), icon: Package },
    { path: '/admin/quotations', label: t('nav.quotations'), icon: ClipboardList },
    { path: '/admin/sales', label: t('nav.sales'), icon: ShoppingCart },
    { path: '/admin/prospects', label: t('nav.prospects'), icon: Target },
    { path: '/admin/cotizaia', label: t('nav.cotizaia'), icon: Sparkles },
    { path: '/admin/users', label: t('nav.users'), icon: Users },
    { path: '/admin/settings', label: t('nav.settings'), icon: Settings },
  ]

  const isActive = (path: string) => {
    if (path === '/admin/catalog' && (location.pathname === '/admin' || location.pathname === '/admin/')) return true
    return location.pathname.startsWith(path)
  }

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 h-16 border-b border-white/5 shrink-0">
        <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center shrink-0">
          <ChefHat className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <p className="text-sm font-bold text-white tracking-tight leading-tight">ChefMax</p>
            <p className="text-[10px] text-primary-400 font-medium tracking-wider uppercase">
              {user?.role === 'superadmin' ? t('role.superadmin') : t('layout.adminPanel')}
            </p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 scrollbar-thin">
        {menuItems.map((item) => {
          const active = isActive(item.path)
          const Icon = item.icon
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`
                flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all
                ${active
                  ? 'bg-primary-600/20 text-white ring-1 ring-primary-500/30'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }
                ${collapsed ? 'justify-center' : ''}
              `}
              title={collapsed ? item.label : undefined}
            >
              <Icon className={`w-5 h-5 shrink-0 ${active ? 'text-primary-300' : ''}`} strokeWidth={2} />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {active && !collapsed && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-400" />
              )}
            </Link>
          )
        })}
      </nav>

      {/* User footer */}
      <div className="border-t border-white/5 p-3 shrink-0">
        <div
          onClick={handleLogout}
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-all hover:bg-red-500/10 group ${collapsed ? 'justify-center' : ''}`}
          title={t('layout.logout')}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-xs font-bold text-white shrink-0">
            {(user?.firstName || user?.email || 'A')[0].toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">
                {user ? `${user.firstName} ${user.lastName?.[0] || ''}`.trim() : t('common.user')}
              </p>
              <p className="text-[10px] text-gray-500 truncate">
                {user?.role === 'superadmin' ? t('role.superadminLong') :
                 user?.role === 'admin' ? t('role.adminLong') : t('common.user')}
              </p>
            </div>
          )}
          {!collapsed && (
            <LogOut className="w-4 h-4 text-gray-500 group-hover:text-red-400 transition-colors" />
          )}
        </div>
      </div>
    </>
  )

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop Sidebar */}
      <aside
        className={`
          hidden md:flex flex-col bg-primary-950 transition-all duration-300 shrink-0
          ${collapsed ? 'w-[68px]' : 'w-64'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="md:hidden fixed left-0 top-0 bottom-0 w-64 bg-primary-950 z-50 flex flex-col animate-slide-in">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden sm:block">
              <h1 className="text-sm font-semibold text-gray-900">
                {menuItems.find(i => isActive(i.path))?.label || t('nav.catalog')}
              </h1>
              <p className="text-xs text-gray-400">ChefMax LATAM</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-100">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
              <span className="text-xs font-medium text-primary-700">{t('layout.online')}</span>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-xs font-bold text-white">
              {(user?.firstName || user?.email || 'A')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
